<template>
  <b-sidebar
    id="add-new-expense-sidebar"
    :visible="isAddNewExpenseSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    right
    @hidden="resetForm"
    @change="(val) => $emit('update:is-add-new-expense-sidebar-active', val)"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0">{{ $t("message.expenses.createNewExpenses") }}</h5>

        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="hide"
        />
      </div>

      <!-- BODY -->
      <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
        <!-- Form -->
        <b-form
          class="p-2"
          @submit.prevent="handleSubmit(submitNewexpense())"
          @reset.prevent="resetForm"
        >
          <!-- Description -->
          <validation-provider
            #default="validationContext"
            name="descrição"
            rules="required"
          >
            <b-form-group
              label-for="description"
              :state="getValidationState(validationContext)"
            >
              <template v-slot:label>
                {{ $t("message.projects.description") }}
              </template>
              <quill-editor
                id="title"
                style="margin-bottom: 82px; height: 300px"
                :options="editorOption"
                v-model="expenseData.description"
              />
            </b-form-group>
          </validation-provider>

          <!-- Date -->
          <validation-provider
            #default="validationContext"
            name="data"
            rules="required"
          >
            <b-form-group label-for="date">
              <template v-slot:label>
                {{ $t("message.bills.expiryDate") }}
              </template>
              <flat-pickr
                v-model="expenseData.date"
                class="form-control"
                :config="flatConfig"
              />
              <b-form-invalid-feedback
                :state="getValidationState(validationContext)"
              >
                <template>
                  {{ $t("message.list.fieldRequired") }}
                </template>
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Type -->
          <validation-provider
            #default="validationContext"
            name="tipo"
            rules="required"
          >
            <b-form-group
              label-for="type"
              :state="getValidationState(validationContext)"
            >
              <template v-slot:label>
                {{ $t("message.tasks.type") }}
              </template>
              <v-select
                v-model="expenseData.type"
                :options="typeOptions"
                :reduce="(val) => val.value"
                :clearable="false"
                input-id="type-status"
              />
            </b-form-group>
          </validation-provider>

          <!-- Payment -->
          <validation-provider
            #default="validationContext"
            name="pagamento"
            rules="required"
          >
            <b-form-group
              label-for="payment"
              :state="getValidationState(validationContext)"
            >
              <template v-slot:label>
                {{ $t("message.expenses.paymentMethod") }}
              </template>
              <v-select
                v-model="expenseData.paymentType"
                :options="paymentOptions"
                :reduce="(val) => val.value"
                :clearable="false"
                input-id="payment-status"
              />
            </b-form-group>
          </validation-provider>

          <!-- Value -->

          <validation-provider
            #default="validationContext"
            name="valor"
            rules="required"
          >
            <b-form-group
              label-for="value"
              :state="getValidationState(validationContext)"
            >
              <template v-slot:label>
                {{ $t("message.expenses.value") }}
              </template>
              <b-form-input
                id="value"
                v-money="getCurrencyFormat()"
                v-model="expenseData.value"
              />
            </b-form-group>
          </validation-provider>

          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2"
              type="submit"
              v-if="!disabledButton"
            >
              {{ $t("message.buttons.create") }}
            </b-button>

            <b-button
              v-else
              disabled
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2"
            >
              {{ $t("message.buttons.create") }}
            </b-button>

            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="button"
              variant="outline-secondary"
              @click="hide"
            >
              {{ $t("message.buttons.cancel") }}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
/* eslint-disable */
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BButton,
  BFormSpinbutton,
  BFormTextarea,
} from "bootstrap-vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { ref } from "@vue/composition-api";
import { required, alphaNum } from "@validations";
import formValidation from "@core/comp-functions/forms/form-validation";
import Ripple from "vue-ripple-directive";
import vSelect from "vue-select";
import getHeader from "@core/utils/auth";
import axios from "@axios";
import URL_API from "@core/utils/env";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import flatPickr from "vue-flatpickr-component";
import { VMoney } from "v-money";
import currency from "@/@core/utils/currency";
import date from "@/@core/utils/date";
import dateInput from "@/@core/utils/dateInput";
import store from "@/store";
import { quillEditor } from "vue-quill-editor";
import "quill/dist/quill.core.css";
import "quill/dist/quill.snow.css";

export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    vSelect,
    flatPickr,
    BFormSpinbutton,
    BFormTextarea,

    // Form Validation
    ValidationProvider,
    ValidationObserver,
    quillEditor,
  },
  directives: {
    Ripple,
    money: VMoney,
  },
  model: {
    prop: "isAddNewExpenseSidebarActive",
    event: "update:is-add-new-expense-sidebar-active",
  },
  props: {
    isAddNewExpenseSidebarActive: {
      type: Boolean,
      required: true,
    },
    typeOptions: {
      type: Array,
      required: true,
    },
    paymentOptions: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      required,
      alphaNum,
      flatConfig: {
        dateFormat: "d/m/Y",
      },

      editorOption: {
        placeholder: "",
        modules: {
          toolbar: [
            ["bold", "italic", "underline"],
            ["code-block"],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ header: [1, 2, 3, 4, 5, 6, false] }],
            [{ color: [] }, { background: [] }],
          ],
          syntax: {
            highlight: (text) => hljs.highlightAuto(text).value,
          },
        },
      },

      //Param
      paramData: store.state.user.paramData,

      userData: store.state.user.userData,

      disabledButton: false,
    };
  },

  watch: {
    "$i18n.locale": function (newVal, oldVal) {
      this.flatConfig = dateInput(newVal);
    },
  },

  mounted() {
    if (this.$i18n.locale) {
      this.flatConfig = dateInput(this.$i18n.locale);
    }
  },

  methods: {
    getCurrencyFormat() {
      return currency(this.$i18n.locale);
    },

    submitNewexpense() {
      this.$refs.refFormObserver.validate().then((success) => {
        if (success) {
          this.disabledButton = true;
          this.onSubmit();

          this.expenseData.date = date(
            this.$i18n.locale,
            this.expenseData.date
          );

          if (this.$i18n.locale === "en") {
            this.expenseData.value = Number(
              this.expenseData.value.replace(/[^0-9\.-]+/g, "")
            ).toFixed(2);
          } else {
            this.expenseData.value = this.switchDotsAndCommas(
              this.expenseData.value
            );
            this.expenseData.value = Number(
              this.expenseData.value.replace(/[^0-9\.-]+/g, "")
            ).toFixed(2);
          }

          axios({
            method: "post",
            url: `${URL_API}expense`,
            headers: getHeader(this.userData),
            data: this.expenseData,
          })
            .then(() => {
              this.$toast({
                component: ToastificationContent,
                position: "top-right",
                props: {
                  title: this.$i18n.t("message.toast.create"),
                  icon: "CheckIcon",
                  variant: "success",
                  text: `Despesa criada com sucesso`,
                },
              });
            })
            .then(() => {
              this.$root.$emit("newListExpense", null);
              this.disabledButton = false;
            })
            .catch((error) => {
              this.disabledButton = false;
              console.log(error);
            });
        }
      });
    },

    switchDotsAndCommas(s) {
      function switcher(match) {
        return match == "," ? "." : ",";
      }
      return s.replaceAll(/\.|\,/g, switcher);
    },
  },

  setup(props, { emit }) {
    const blankexpenseData = {};

    const expenseData = ref(JSON.parse(JSON.stringify(blankexpenseData)));
    const resetexpenseData = () => {
      expenseData.value = JSON.parse(JSON.stringify(blankexpenseData));
    };

    const onSubmit = () => {
      emit("refetch-data");
      emit("update:is-add-new-expense-sidebar-active", false);
    };

    const { refFormObserver, getValidationState, resetForm } =
      formValidation(resetexpenseData);

    return {
      expenseData,
      onSubmit,

      refFormObserver,
      getValidationState,
      resetForm,
    };
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

#add-new-expense-sidebar {
  .vs__dropdown-menu {
    max-height: 200px !important;
  }
}
</style>
